import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { ChevronRight, Image as ImageIcon, MoreVertical, Plus, X, ZoomIn } from 'lucide-react'
import { ExistingAttachment } from '../../../../components/AttachmentManager'
import {
  AsideAddTile,
  AsideContainer,
  AsideContent,
  AsideEmptyState,
  AsideHeader,
  AsideTab,
  AsideTabs,
  AsideThumb,
  AsideThumbStrip,
  AttachmentDropdownItem,
  AttachmentDropdownPortal,
  CloseButton,
  CollapsedPill,
  CollapsedPillIcon,
  CollapsedPillLabel,
  CoverBadge,
  DocumentRow,
  DocumentRowAction,
  DocumentRowActions,
  DocumentRowName,
  DocumentsList,
  FileBadge,
  IndexBadge,
  PreviewArea,
  PreviewEmpty,
  ThumbMenuButton,
} from './attachmentsAside.style'
import eye from '../../../../assets/fashion/OrderForm/eye.svg'
import downloadIcon from '../../../../assets/fashion/OrderForm/download.svg'
import crossIcon from '../../../../assets/fashion/OrderForm/cross.svg'
import CoverScaleModal from './CoverScaleModal'
import ImageLightbox from '../../../../components/ImageViewer'
import AttachmentSourceModal from '../../../../components/AttachmentSourceModal'
import AttachmentGalleryModal, { GalleryFile } from '../../../../components/AttachmentGalleryModal'
import { openSignedAttachment, getSignedAttachmentUrl } from '../../../../utils/openSignedAttachment'
import { downloadRemoteFile } from '../../../../utils/downloadRemoteFile'

export type AttachmentTab = 'images' | 'documents'

/** Extensões e MIME-types aceitos na aba Documentos (input + validação no pai). */
export const DOCUMENT_FILE_ACCEPT = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.rtf',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/rtf',
].join(',')

const DOCUMENT_EXT = new Set([
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'rtf',
])

export const isProductImageFile = (file: File) => {
  if (file.type && file.type.toLowerCase().startsWith('image/')) return true
  return /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif)$/i.test(file.name || '')
}

export const isAllowedDocumentUpload = (file: File) => {
  const name = (file.name || '').toLowerCase()
  const parts = name.split('.')
  const ext = parts.length > 1 ? parts.pop() || '' : ''
  if (ext && DOCUMENT_EXT.has(ext)) return true

  const mt = (file.type || '').toLowerCase()
  if (!mt) return false

  if (mt === 'application/pdf') return true
  if (mt === 'application/msword') return true
  if (mt === 'application/rtf') return true
  if (mt === 'text/plain' || mt === 'text/csv') return true
  if (mt.includes('wordprocessingml')) return true
  if (mt.includes('spreadsheetml') || mt === 'application/vnd.ms-excel') return true
  if (mt.includes('presentationml') || mt === 'application/vnd.ms-powerpoint') return true

  return false
}

interface ProductAttachmentsAsideProps {
  selectedFiles: File[]
  existingAttachments: ExistingAttachment[]
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (index: number) => void
  onViewAttachment?: (attachment: ExistingAttachment) => void
  onDownloadAttachment?: (attachment: ExistingAttachment) => void
  onRemoveExistingAttachment?: (attachmentId: number) => void
  onSetCoverAttachment?: (attachment: ExistingAttachment, scale: number, posX: number, posY: number) => void
  onRemoveCoverAttachment?: (attachment: ExistingAttachment) => void
  onSelectFromGallery?: (attachments: ExistingAttachment[]) => void
  formatFileSize?: (bytes: number) => string
  isOpen: boolean
  onToggleOpen: (next: boolean) => void
}

const isImageMime = (mime?: string) =>
  Boolean(mime && mime.toLowerCase().startsWith('image/'))

const hasImageExtension = (name?: string) =>
  /\.(png|jpe?g|gif|bmp|webp|svg|avif|heic|heif)$/i.test(name || '')

const isExistingImage = (attachment: ExistingAttachment) => {
  if (attachment.isImage === true) return true
  if (isImageMime(attachment.mimeType)) return true
  return hasImageExtension(
    attachment.originalName ||
      attachment.fileName ||
      attachment.fileUrl ||
      attachment.filePath,
  )
}

const isFileImage = (file: File) => isProductImageFile(file)

const getAttachmentSource = (attachment: ExistingAttachment) =>
  attachment.filePath || attachment.fileUrl || ''

const getExtensionBadge = (name?: string) => {
  const source = name || ''
  const extension = source.split('.').pop()
  if (!extension) return 'ARQ'
  return extension.slice(0, 4).toUpperCase()
}

const defaultFormatFileSize = (bytes: number) => `${(bytes / 1024).toFixed(1)} KB`

interface ImageItem {
  key: string
  isExisting: boolean
  attachment?: ExistingAttachment
  file?: File
  fileIndex?: number
  previewUrl?: string
  isCover: boolean
  displayName: string
}

const ProductAttachmentsAside: React.FC<ProductAttachmentsAsideProps> = ({
  selectedFiles,
  existingAttachments,
  onFileChange,
  onRemoveFile,
  onViewAttachment,
  onDownloadAttachment,
  onRemoveExistingAttachment,
  onSetCoverAttachment,
  onRemoveCoverAttachment,
  onSelectFromGallery,
  formatFileSize = defaultFormatFileSize,
  isOpen,
  onToggleOpen,
}) => {
  const [activeTab, setActiveTab] = useState<AttachmentTab>('images')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null)
  const [pendingCoverKey, setPendingCoverKey] = useState<string | null>(null)
  const [isSourceChoiceOpen, setIsSourceChoiceOpen] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [pendingSourceTab, setPendingSourceTab] = useState<AttachmentTab>('images')
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; transform?: string }>({
    top: 0,
    left: 0,
  })
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const dropdownRef = useRef<HTMLDivElement | null>(null)

  const [signedPreviewMap, setSignedPreviewMap] = useState<Record<string, string>>({})

  useEffect(() => {
    let isCancelled = false
    const toSign = existingAttachments.filter((att) => {
      const src = att.filePath || att.fileUrl || ''
      return (
        isExistingImage(att) &&
        src &&
        (src.includes('storage.googleapis.com') ||
          src.includes('storage.cloud.google.com') ||
          src.startsWith('gs://')) &&
        !src.includes('X-Goog-Signature')
      )
    })

    if (!toSign.length) return

    toSign.forEach(async (att) => {
      const src = att.filePath || att.fileUrl || ''
      try {
        const signed = await getSignedAttachmentUrl(
          src,
          false,
          att.fileName || att.originalName,
        )
        if (signed && signed !== src && !isCancelled) {
          setSignedPreviewMap((prev) => ({
            ...prev,
            [`existing-${att.id}`]: signed,
          }))
        }
      } catch (error) {
        console.warn(error)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [existingAttachments])

  const imageItems = useMemo<ImageItem[]>(() => {
    const fromExisting: ImageItem[] = existingAttachments
      .filter(isExistingImage)
      .map((attachment) => {
        const key = `existing-${attachment.id}`
        const rawSource = getAttachmentSource(attachment)
        const previewUrl = signedPreviewMap[key] || rawSource || undefined
        return {
          key,
          isExisting: true,
          attachment,
          previewUrl,
          isCover: Boolean(attachment.isCover),
          displayName: attachment.originalName || attachment.fileName || 'imagem',
        }
      })

    const fromSelected: ImageItem[] = selectedFiles
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => isFileImage(file))
      .map(({ file, index }) => ({
        key: `selected-${index}`,
        isExisting: false,
        file,
        fileIndex: index,
        previewUrl: URL.createObjectURL(file),
        isCover: false,
        displayName: file.name,
      }))

    return [...fromExisting, ...fromSelected]
  }, [existingAttachments, selectedFiles, signedPreviewMap])

  const documentExisting = useMemo(
    () => existingAttachments.filter((a) => !isExistingImage(a)),
    [existingAttachments],
  )
  const documentSelected = useMemo(
    () =>
      selectedFiles
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => !isFileImage(file)),
    [selectedFiles],
  )

  const lightboxSlides = useMemo(() => {
    return imageItems
      .filter((item): item is typeof item & { previewUrl: string } => Boolean(item.previewUrl))
      .map((item) => ({
        src: item.previewUrl,
        title: item.displayName,
      }))
  }, [imageItems])

  useEffect(() => {
    return () => {
      imageItems.forEach((item) => {
        if (!item.isExisting && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl)
        }
      })
    }
  }, [imageItems])

  useEffect(() => {
    if (!imageItems.length) {
      setSelectedKey(null)
      return
    }

    if (selectedKey && imageItems.some((item) => item.key === selectedKey)) {
      return
    }

    const cover = imageItems.find((item) => item.isCover)
    setSelectedKey((cover || imageItems[0]).key)
  }, [imageItems, selectedKey])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      const insideButton = Object.values(menuButtonRefs.current).some(
        (button) => button && button.contains(target),
      )
      if (!insideDropdown && !insideButton) {
        setActiveMenuKey(null)
      }
    }

    const handleScrollOrResize = () => setActiveMenuKey(null)

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const triggerFilePicker = (accept: string) => {
    if (!fileInputRef.current) return
    fileInputRef.current.value = ''
    fileInputRef.current.accept = accept
    fileInputRef.current.click()
  }

  const handleOpenAdd = (tab: AttachmentTab) => {
    setPendingSourceTab(tab)
    setIsSourceChoiceOpen(true)
  }

  const handleToggleMenu = (key: string, button: HTMLButtonElement | null) => {
    if (activeMenuKey === key) {
      setActiveMenuKey(null)
      return
    }

    if (button) {
      const rect = button.getBoundingClientRect()
      const menuWidth = 220
      const menuHeight = 220
      let left = rect.left
      let top = rect.bottom + 5
      let transform = undefined

      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 10
      }
      if (left < 10) left = 10
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - 5
        transform = 'translateY(-100%)'
      }
      if (top < 10) top = 10
      setMenuPosition({ top, left, transform })
    }

    setActiveMenuKey(key)
  }

  const selectedItem = useMemo(
    () => imageItems.find((item) => item.key === selectedKey) || null,
    [imageItems, selectedKey],
  )

  const selectedItemIndex = useMemo(() => {
    if (!selectedItem) return -1
    return imageItems.findIndex((item) => item.key === selectedItem.key)
  }, [imageItems, selectedItem])

  const pendingCoverItem = useMemo(
    () => imageItems.find((item) => item.key === pendingCoverKey) || null,
    [imageItems, pendingCoverKey],
  )

  if (!isOpen) {
    return (
      <CollapsedPill
        type='button'
        onClick={() => onToggleOpen(true)}
        title='Mostrar anexos'
      >
        <CollapsedPillIcon>
          <ImageIcon size={18} />
        </CollapsedPillIcon>
        <CollapsedPillLabel>Anexos</CollapsedPillLabel>
        <ChevronRight size={16} />
      </CollapsedPill>
    )
  }

  const renderImagesTab = () => {
    return (
      <>
        <PreviewArea
          style={{ cursor: selectedItem?.previewUrl ? 'pointer' : 'default' }}
          onClick={() => {
            if (!selectedItem?.previewUrl) return
            const targetIndex = lightboxSlides.findIndex((s) => s.src === selectedItem.previewUrl)
            setLightboxIndex(Math.max(0, targetIndex))
            setLightboxOpen(true)
          }}
        >
          {selectedItem ? (
            <>
              {selectedItemIndex >= 0 && (
                <IndexBadge>{selectedItemIndex + 1}</IndexBadge>
              )}
              {selectedItem.isCover && <CoverBadge>Capa</CoverBadge>}
              {selectedItem.previewUrl ? (
                <>
                  <img
                    src={selectedItem.previewUrl}
                    alt={selectedItem.displayName}
                  />
                  <div className='zoom-overlay'>
                    <ZoomIn size={22} />
                    <span>Clique para aumentar</span>
                  </div>
                </>
              ) : (
                <FileBadge>
                  {getExtensionBadge(selectedItem.displayName)}
                </FileBadge>
              )}
            </>
          ) : (
            <PreviewEmpty>
              <ImageIcon size={32} />
              <span>Nenhuma imagem ainda</span>
              <small>Use o botão + abaixo para adicionar</small>
            </PreviewEmpty>
          )}
        </PreviewArea>

        <AsideThumbStrip>
          {imageItems.map((item) => {
            const isActive = item.key === selectedKey
            return (
              <AsideThumb
                key={item.key}
                $active={isActive}
                onClick={() => setSelectedKey(item.key)}
              >
                {item.previewUrl ? (
                  <img src={item.previewUrl} alt={item.displayName} />
                ) : (
                  <FileBadge>{getExtensionBadge(item.displayName)}</FileBadge>
                )}
                <ThumbMenuButton
                  ref={(element) => {
                    menuButtonRefs.current[item.key] = element
                  }}
                  type='button'
                  onClick={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    handleToggleMenu(item.key, menuButtonRefs.current[item.key])
                  }}
                >
                  <MoreVertical size={12} />
                </ThumbMenuButton>
              </AsideThumb>
            )
          })}

          <AsideAddTile
            type='button'
            onClick={() => handleOpenAdd('images')}
            title='Adicionar imagem'
          >
            <Plus size={18} />
          </AsideAddTile>
        </AsideThumbStrip>
      </>
    )
  }

  const renderDocumentsTab = () => {
    if (!documentExisting.length && !documentSelected.length) {
      return (
        <>
          <AsideEmptyState>
            <span>Nenhum documento</span>
            <small>Use o botão + abaixo para adicionar</small>
          </AsideEmptyState>
          <DocumentsList>
            <AsideAddTile
              type='button'
              onClick={() => handleOpenAdd('documents')}
              title='Adicionar documento'
            >
              <Plus size={18} />
            </AsideAddTile>
          </DocumentsList>
        </>
      )
    }

    return (
      <DocumentsList>
        {documentExisting.map((attachment) => {
          const source = getAttachmentSource(attachment)
          const canDownload = Boolean(onDownloadAttachment || source)

          return (
            <DocumentRow key={`existing-${attachment.id}`}>
              <FileBadge>{getExtensionBadge(attachment.originalName || attachment.fileName)}</FileBadge>
              <DocumentRowName title={attachment.originalName || attachment.fileName}>
                <span>{attachment.originalName || attachment.fileName}</span>
                <small>{formatFileSize(attachment.fileSize || 0)}</small>
              </DocumentRowName>
              <DocumentRowActions>
                {onViewAttachment && (
                  <DocumentRowAction
                    type='button'
                    title='Visualizar'
                    onClick={() => onViewAttachment(attachment)}
                  >
                    <img src={eye} alt='Visualizar' />
                  </DocumentRowAction>
                )}
                {canDownload && (
                  <DocumentRowAction
                    type='button'
                    title='Download'
                    onClick={async () => {
                      if (onDownloadAttachment) {
                        onDownloadAttachment(attachment)
                        return
                      }
                      if (source) {
                        const fileName = attachment.originalName || attachment.fileName || 'anexo'
                        await downloadRemoteFile(source, fileName)
                      }
                    }}
                  >
                    <img src={downloadIcon} alt='Download' />
                  </DocumentRowAction>
                )}
                {onRemoveExistingAttachment && (
                  <DocumentRowAction
                    type='button'
                    title='Remover'
                    onClick={() => onRemoveExistingAttachment(attachment.id)}
                  >
                    <img src={crossIcon} alt='Remover' />
                  </DocumentRowAction>
                )}
              </DocumentRowActions>
            </DocumentRow>
          )
        })}

        {documentSelected.map(({ file, index }) => (
          <DocumentRow key={`selected-${index}`}>
            <FileBadge>{getExtensionBadge(file.name)}</FileBadge>
            <DocumentRowName title={file.name}>
              <span>{file.name}</span>
              <small>{formatFileSize(file.size || 0)} (novo)</small>
            </DocumentRowName>
            <DocumentRowActions>
              <DocumentRowAction
                type='button'
                title='Remover'
                onClick={() => onRemoveFile(index)}
              >
                <img src={crossIcon} alt='Remover' />
              </DocumentRowAction>
            </DocumentRowActions>
          </DocumentRow>
        ))}

        <AsideAddTile
          type='button'
          onClick={() => handleOpenAdd('documents')}
          title='Adicionar documento'
        >
          <Plus size={18} />
        </AsideAddTile>
      </DocumentsList>
    )
  }

  const renderActiveMenu = () => {
    if (!activeMenuKey) return null
    const item = imageItems.find((entry) => entry.key === activeMenuKey)
    if (!item) return null

    const close = () => setActiveMenuKey(null)

    if (item.isExisting && item.attachment) {
      const attachment = item.attachment
      const source = getAttachmentSource(attachment)
      const canDownload = Boolean(onDownloadAttachment || source)

      return ReactDOM.createPortal(
        <AttachmentDropdownPortal
          ref={dropdownRef}
          style={{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }}
          onClick={(event) => event.stopPropagation()}
        >
          {Boolean(onViewAttachment || item.previewUrl) && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                if (item.previewUrl) {
                  const targetIndex = lightboxSlides.findIndex((s) => s.src === item.previewUrl)
                  setLightboxIndex(Math.max(0, targetIndex))
                  setLightboxOpen(true)
                } else if (onViewAttachment) {
                  onViewAttachment(attachment)
                }
                close()
              }}
            >
              Visualizar
            </AttachmentDropdownItem>
          )}
          {canDownload && (
            <AttachmentDropdownItem
              type='button'
              onClick={async () => {
                if (onDownloadAttachment) {
                  onDownloadAttachment(attachment)
                } else if (source) {
                  const fileName = attachment.originalName || attachment.fileName || 'anexo'
                  await downloadRemoteFile(source, fileName)
                }
                close()
              }}
            >
              Download
            </AttachmentDropdownItem>
          )}
          {attachment.isCover && onSetCoverAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                setPendingCoverKey(item.key)
                close()
              }}
            >
              Redimensionar capa
            </AttachmentDropdownItem>
          )}
          {attachment.isCover && onRemoveCoverAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                onRemoveCoverAttachment(attachment)
                close()
              }}
            >
              Remover da capa do produto
            </AttachmentDropdownItem>
          )}
          {!attachment.isCover && onSetCoverAttachment && (
            <AttachmentDropdownItem
              type='button'
              onClick={() => {
                setPendingCoverKey(item.key)
                close()
              }}
            >
              Definir como capa do produto
            </AttachmentDropdownItem>
          )}
          {onRemoveExistingAttachment && (
            <AttachmentDropdownItem
              type='button'
              className='danger'
              onClick={() => {
                onRemoveExistingAttachment(attachment.id)
                close()
              }}
            >
              Excluir
            </AttachmentDropdownItem>
          )}
        </AttachmentDropdownPortal>,
        document.body,
      )
    }

    if (!item.isExisting && typeof item.fileIndex === 'number') {
      const fileIndex = item.fileIndex
      return ReactDOM.createPortal(
        <AttachmentDropdownPortal
          ref={dropdownRef}
          style={{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }}
          onClick={(event) => event.stopPropagation()}
        >
          <AttachmentDropdownItem
            type='button'
            className='danger'
            onClick={() => {
              onRemoveFile(fileIndex)
              close()
            }}
          >
            Remover
          </AttachmentDropdownItem>
        </AttachmentDropdownPortal>,
        document.body,
      )
    }

    return null
  }

  return (
    <AsideContainer>
      <AsideHeader>
        <AsideTabs>
          <AsideTab
            type='button'
            $active={activeTab === 'images'}
            onClick={() => setActiveTab('images')}
          >
            Imagens
          </AsideTab>
          <AsideTab
            type='button'
            $active={activeTab === 'documents'}
            onClick={() => setActiveTab('documents')}
          >
            Documentos
          </AsideTab>
        </AsideTabs>
        <CloseButton
          type='button'
          onClick={() => onToggleOpen(false)}
          title='Recolher anexos'
        >
          <X size={16} />
        </CloseButton>
      </AsideHeader>

      <AsideContent>
        {activeTab === 'images' ? renderImagesTab() : renderDocumentsTab()}
      </AsideContent>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        style={{ display: 'none' }}
        onChange={onFileChange}
      />

      {renderActiveMenu()}

      {pendingCoverItem && pendingCoverItem.previewUrl && onSetCoverAttachment && (
        <CoverScaleModal
          isOpen
          imageUrl={pendingCoverItem.previewUrl}
          imageAlt={pendingCoverItem.displayName}
          initialScale={pendingCoverItem.attachment?.coverScale}
          initialPosX={pendingCoverItem.attachment?.coverPosX}
          initialPosY={pendingCoverItem.attachment?.coverPosY}
          onCancel={() => setPendingCoverKey(null)}
          onConfirm={(scale, posX, posY) => {
            if (pendingCoverItem.attachment) {
              onSetCoverAttachment(pendingCoverItem.attachment, scale, posX, posY)
            }
            setPendingCoverKey(null)
          }}
        />
      )}

      <ImageLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />

      <AttachmentSourceModal
        isOpen={isSourceChoiceOpen}
        onClose={() => setIsSourceChoiceOpen(false)}
        title={pendingSourceTab === 'images' ? 'Adicionar Imagem' : 'Adicionar Documento'}
        description={
          pendingSourceTab === 'images'
            ? 'Escolha como deseja adicionar a imagem ao produto:'
            : 'Escolha como deseja adicionar o documento ao produto:'
        }
        onChooseUpload={() => {
          triggerFilePicker(pendingSourceTab === 'images' ? 'image/*' : DOCUMENT_FILE_ACCEPT)
        }}
        onChooseGallery={() => {
          setIsGalleryOpen(true)
        }}
      />

      <AttachmentGalleryModal
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        multiple={true}
        defaultFilter={pendingSourceTab === 'images' ? 'image' : 'document'}
        title={pendingSourceTab === 'images' ? 'Galeria da Empresa - Imagens' : 'Galeria da Empresa - Documentos'}
        onSelect={(galleryFiles: GalleryFile[]) => {
          if (onSelectFromGallery) {
            const converted: ExistingAttachment[] = galleryFiles.map((f) => ({
              id: f.id,
              originalName: f.originalName || f.title,
              fileName: f.fileName,
              filePath: f.signedUrl || f.fileUrl,
              fileUrl: f.fileUrl,
              fileSize: f.fileSize,
              mimeType: f.mimeType,
              isImage: f.mimeType?.startsWith('image/'),
            }))
            onSelectFromGallery(converted)
          }
        }}
      />
    </AsideContainer>
  )
}

export default ProductAttachmentsAside
